import * as debounce from 'lodash.debounce';
import * as React from 'react';
import { UitkInput } from 'uitk-react-input';
import { Form } from 'components/common/form';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { AnalyticsStore, ContextStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { inject } from 'mobx-react';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { FindAnswersFormLaunchLocation, FindAnswersStore } from 'stores/find-answers-store';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { ClientLogger } from 'bernie-client';
import { FindAnswersResult } from 'components/infosite/find-answers/find-answers-result';

const TrackedTextInput = ClickTracker(UitkInput);

const FIND_ANSWERS_TO_RETRIEVE: number = 10;
const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.findanswers');

export enum FindAnswersResultType {
  PROPERTY_DETAILS,
  REVIEWS,
  QUESTIONS
}

export interface FindAnswersFormProps extends LocalizedComponentProps {
  closeTakeover: () => void;
  onShowResults?: () => void;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  findAnswersStore?: FindAnswersStore;
  analytics?: AnalyticsStore;
}

export interface FindAnswersFormState {
  searchString: string;
  showResults?: boolean;
  propertyDetailsStartIndex: number;
  errorFetching?: boolean;
}

@withLocalization
@inject('context', 'currentHotel', 'findAnswersStore', 'analytics')
export class FindAnswersForm extends React.Component<FindAnswersFormProps, FindAnswersFormState> {
  private logUserInputDebounced;
  public inputFieldRef: React.RefObject<HTMLInputElement> = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      searchString: '',
      propertyDetailsStartIndex: FIND_ANSWERS_TO_RETRIEVE
    };

    this.handleChange = this.handleChange.bind(this);
    this.logUserInput = this.logUserInput.bind(this);
    this.buildClientLog = this.buildClientLog.bind(this);
    this.logUserInputDebounced = debounce(this.logUserInput, 500);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleMorePropertyDetails = this.handleMorePropertyDetails.bind(this);
    this.handleMoreReviews = this.handleMoreReviews.bind(this);
    this.handleMoreQuestions = this.handleMoreQuestions.bind(this);
    this.propertyHasUserReviews = this.propertyHasUserReviews.bind(this);
    this.focusInputText = this.focusInputText.bind(this);
  }

  public componentDidUpdate() {
    if (!this.state.showResults) {
      this.focusInputText(this.inputFieldRef);
    }
  }

  public focusInputText(curInputTextRef) {
    if (curInputTextRef && curInputTextRef.current && typeof curInputTextRef.current.focus === 'function') {
      curInputTextRef.current.focus();
    }
  }

  private buildClientLog() {
    const { currentHotel, findAnswersStore } = this.props;
    const { searchString } = this.state;

    const propertyDetailsSize = currentHotel.findAnswersSearchData && currentHotel.findAnswersSearchData.length;
    const reviewsSize = findAnswersStore && findAnswersStore.reviews && findAnswersStore.reviews.count;
    const questionAnswersSize = findAnswersStore && findAnswersStore.questionAnswers && findAnswersStore.questionAnswers.count;

    return {
      hotelId: `${currentHotel.hotelId}`,
      textInputValue: `${searchString}`,
      propertyDetailsResultsSize: propertyDetailsSize || 0,
      reviewsSize: reviewsSize || 0,
      questionAnswersSize: questionAnswersSize || 0
    };
  }

  public logUserInput() {
    logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'FINDANSWERS_RESULTS'), this.buildClientLog());
  }

  public handleChange(event) {
    const searchString = event && event.target && event.target.value;

    this.setState({
      searchString
    });
  }

  public handleMorePropertyDetails() {
    const startIndex = this.state.propertyDetailsStartIndex;
    this.setState({ propertyDetailsStartIndex: startIndex + FIND_ANSWERS_TO_RETRIEVE });
  }

  public handleMoreReviews(): void {
    const { context, currentHotel, findAnswersStore } = this.props;
    const { searchString } = this.state;
    findAnswersStore.fetchReviews(context, currentHotel.hotelId, searchString);
  }

  public handleMoreQuestions(): void {
    const { context, currentHotel, findAnswersStore } = this.props;
    const { searchString } = this.state;
    findAnswersStore.fetchQuestionAnswers(context, currentHotel.hotelId, searchString);
  }

  public handleSubmit(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    const { searchString } = this.state;
    if (!searchString || searchString.length === 0) {
      return;
    }

    const { context, currentHotel, findAnswersStore, onShowResults, analytics } = this.props;
    const questionAnswersViewModel = currentHotel.hotel && currentHotel.hotel.questionAnswersViewModel;
    const enabledForConventionalLodging = questionAnswersViewModel && questionAnswersViewModel.enabledForConventionalLodging;
    if (enabledForConventionalLodging) {
      findAnswersStore
        .fetchFindAnswers(context, currentHotel.hotelId, searchString)
        .catch(() => {
          this.setState({ errorFetching: true });
        })
        .finally(() => {
          currentHotel.getPropertyInfoForKeyword(searchString);
        })
        .finally(() => {
          this.setState({ showResults: true }, () => {
            if (typeof onShowResults === 'function') {
              onShowResults();
            }
          });
        });
    } else {
      currentHotel.getPropertyInfoForKeyword(searchString);
      this.setState({ showResults: true }, () => {
        if (typeof onShowResults === 'function') {
          onShowResults();
        }
      });
    }

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('AskQuestion.Search', 'Search for input text', {});
    }

    this.logUserInputDebounced();
  }

  private propertyHasUserReviews(): boolean {
    const { currentHotel } = this.props;

    return currentHotel.reviews && currentHotel.reviews.count > 0;
  }

  public render() {
    const { searchString, showResults, propertyDetailsStartIndex, errorFetching } = this.state;
    const { l10n, currentHotel, findAnswersStore } = this.props;
    const { formLaunchLocation } = findAnswersStore;
    const showReviewTab =
      (formLaunchLocation && formLaunchLocation === FindAnswersFormLaunchLocation.REVIEWS_DIALOG) || this.propertyHasUserReviews();
    const questionAnswersViewModel = currentHotel.hotel && currentHotel.hotel.questionAnswersViewModel;
    const enabledForConventionalLodging = questionAnswersViewModel && questionAnswersViewModel.enabledForConventionalLodging;

    return (
      <React.Fragment>
        <Form onSubmit={this.handleSubmit} noValidate={true}>
          <div className="uitk-type-heading-300">{l10n.formatText('hotels.infosite.findAnswers.form.search.subHeader')}</div>
          <div className="uitk-cell all-y-padding-three">
            <TrackedTextInput
              value={searchString}
              onChange={this.handleChange}
              autoFocus={true}
              label={l10n.formatText('hotels.infosite.findAnswers.form.search.label')}
              icon="search"
              type="search"
              id="search-find-answers"
              name="search"
              hasVisibleLabel={true}
              hasClearButton={true}
              domRef={this.inputFieldRef}
              rfrr="AskQuestion.Search"
            />
          </div>
        </Form>
        {showResults && (
          <React.Fragment>
            <FindAnswersResult
              propertyDetails={currentHotel.findAnswersSearchData}
              reviews={findAnswersStore.reviews}
              questionAnswers={findAnswersStore.questionAnswers}
              propertyDetailsStartIndex={propertyDetailsStartIndex}
              searchString={searchString}
              handleMorePropertyDetails={this.handleMorePropertyDetails}
              handleMoreReviews={this.handleMoreReviews}
              handleMoreQuestions={this.handleMoreQuestions}
              errorFetching={errorFetching}
              formLaunchLocation={formLaunchLocation}
              showReviewTab={showReviewTab}
              enabledForConventionalLodging={enabledForConventionalLodging}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}
